(() => {
  const HIGH_TO_LOW = 'discount-descending';
  const LOW_TO_HIGH = 'discount-ascending';
  const SERVER_SORT = 'created-descending';
  const PAGE_SIZE_FALLBACK = 32;
  const MAX_CONCURRENT_REQUESTS = 4;

  let requestController = null;
  let infiniteObserver = null;
  let refreshTimer = null;
  let generation = 0;

  const isDiscountSort = (value) => value === HIGH_TO_LOW || value === LOW_TO_HIGH;

  const currentSort = () => new URLSearchParams(window.location.search).get('sort_by');

  const syncSortSelect = () => {
    const value = currentSort();
    if (!isDiscountSort(value)) return;

    document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      if (select.querySelector(`option[value="${value}"]`)) {
        select.value = value;
      }
    });
  };

  const getSectionId = () => document.getElementById('product-grid')?.dataset.id;

  const createSectionUrl = (sectionId, page) => {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('sort_by');
    params.set('sort_by', SERVER_SORT);
    params.set('section_id', sectionId);
    params.set('page', String(page));
    return `${window.location.pathname}?${params.toString()}`;
  };

  const fetchSectionDocument = async (sectionId, page, signal) => {
    const response = await fetch(createSectionUrl(sectionId, page), {
      credentials: 'same-origin',
      signal,
    });

    if (!response.ok) {
      throw new Error(`Discount sort request failed with status ${response.status}`);
    }

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const getTotalPages = (doc) => {
    const paginationData = doc.querySelector('[data-total-pages]');
    const totalPages = Number.parseInt(paginationData?.dataset.totalPages || '1', 10);
    if (Number.isFinite(totalPages) && totalPages > 1) return totalPages;

    const paginationPages = Array.from(
      doc.querySelectorAll('.pagination-wrapper a[href*="page="]')
    ).map((link) => {
      const page = new URL(link.href, window.location.origin).searchParams.get('page');
      return Number.parseInt(page || '1', 10);
    });

    return Math.max(1, ...paginationPages.filter(Number.isFinite));
  };

  const getProductCards = (doc) =>
    Array.from(doc.querySelectorAll('#product-grid > .collection-product-card'));

  const fetchAllFilteredPages = async (sectionId, signal) => {
    const firstDocument = await fetchSectionDocument(sectionId, 1, signal);
    const totalPages = getTotalPages(firstDocument);
    const documents = new Array(totalPages);
    documents[0] = firstDocument;

    let nextPage = 2;
    const workers = Array.from(
      { length: Math.min(MAX_CONCURRENT_REQUESTS, Math.max(0, totalPages - 1)) },
      async () => {
        while (nextPage <= totalPages) {
          const page = nextPage;
          nextPage += 1;
          documents[page - 1] = await fetchSectionDocument(sectionId, page, signal);
        }
      }
    );

    await Promise.all(workers);
    return documents;
  };

  const toSortableProduct = (node, originalIndex) => {
    const price = Number(node.dataset.price);
    const compareAtPrice = Number(node.dataset.compareAtPrice);
    const valid =
      Number.isFinite(price) &&
      Number.isFinite(compareAtPrice) &&
      compareAtPrice > 0 &&
      compareAtPrice > price;

    return {
      node: node.cloneNode(true),
      originalIndex,
      valid,
      discountPercentage: valid
        ? ((compareAtPrice - price) / compareAtPrice) * 100
        : null,
    };
  };

  const sortProducts = (products, direction) => {
    const descending = direction === HIGH_TO_LOW;

    return products.sort((a, b) => {
      if (a.valid !== b.valid) return a.valid ? -1 : 1;
      if (!a.valid) return a.originalIndex - b.originalIndex;

      const difference = descending
        ? b.discountPercentage - a.discountPercentage
        : a.discountPercentage - b.discountPercentage;

      return difference || a.originalIndex - b.originalIndex;
    });
  };

  const removeNativePagination = (container) => {
    container
      .querySelectorAll(
        '.infinite-scroll__data, .js-infinite-scroll, .pagination-wrapper'
      )
      .forEach((element) => element.remove());
  };

  const reinitializeProductCards = () => {
    if (typeof window.colorSwatches === 'function') window.colorSwatches();
    if (typeof window.loadeMoreSlider_G === 'function') window.loadeMoreSlider_G();

    const FacetFiltersForm = customElements.get('facet-filters-form');
    if (typeof FacetFiltersForm?.rerenderProductSliders === 'function') {
      FacetFiltersForm.rerenderProductSliders();
    }
  };

  const disconnectInfiniteObserver = () => {
    if (infiniteObserver) {
      infiniteObserver.disconnect();
      infiniteObserver = null;
    }

    document.querySelector('.discount-sort-infinite-scroll')?.remove();
  };

  const renderSortedProducts = (products, pageSize, container, grid) => {
    disconnectInfiniteObserver();
    grid.replaceChildren();

    let renderedCount = 0;

    const appendBatch = () => {
      const fragment = document.createDocumentFragment();
      const end = Math.min(renderedCount + pageSize, products.length);

      for (let index = renderedCount; index < end; index += 1) {
        fragment.appendChild(products[index].node);
      }

      grid.appendChild(fragment);
      renderedCount = end;
      reinitializeProductCards();

      if (renderedCount >= products.length) {
        disconnectInfiniteObserver();
      }
    };

    appendBatch();

    if (renderedCount < products.length) {
      const sentinel = document.createElement('div');
      sentinel.className = 'discount-sort-infinite-scroll';
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.height = '1px';
      grid.insertAdjacentElement('afterend', sentinel);

      infiniteObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) appendBatch();
        },
        { rootMargin: '0px 0px 800px 0px' }
      );

      infiniteObserver.observe(sentinel);
    }

    container.dataset.discountSortCount = String(products.length);
  };

  const setLoading = (container, loading) => {
    const collection = container.querySelector('.collection');
    collection?.classList.toggle('loading', loading);
    container.toggleAttribute('aria-busy', loading);
  };

  const runDiscountSort = async () => {
    const direction = currentSort();
    if (!isDiscountSort(direction)) return;

    const sectionId = getSectionId();
    const container = document.getElementById('ProductGridContainer');
    if (!sectionId || !container) return;

    syncSortSelect();
    const runGeneration = ++generation;
    requestController?.abort();
    requestController = new AbortController();
    setLoading(container, true);

    try {
      const documents = await fetchAllFilteredPages(
        sectionId,
        requestController.signal
      );

      if (runGeneration !== generation || direction !== currentSort()) return;

      const firstPageCount = getProductCards(documents[0]).length;
      const pageSize = firstPageCount || PAGE_SIZE_FALLBACK;
      const products = documents
        .flatMap((doc) => getProductCards(doc))
        .map(toSortableProduct);

      sortProducts(products, direction);

      const currentContainer = document.getElementById('ProductGridContainer');
      const currentGrid = document.getElementById('product-grid');
      if (!currentContainer || !currentGrid) return;

      removeNativePagination(currentContainer);
      renderSortedProducts(products, pageSize, currentContainer, currentGrid);
      syncSortSelect();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[Summer Clearance discount sort]', error);
      }
    } finally {
      if (runGeneration === generation) {
        const currentContainer = document.getElementById('ProductGridContainer');
        if (currentContainer) setLoading(currentContainer, false);
      }
    }
  };

  const teardownDiscountSort = () => {
    generation += 1;
    requestController?.abort();
    requestController = null;
    disconnectInfiniteObserver();
  };

  const queueDiscountSort = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      syncSortSelect();
      if (isDiscountSort(currentSort())) {
        runDiscountSort();
      } else {
        teardownDiscountSort();
      }
    }, 0);
  };

  const patchFacetRendering = () => {
    const FacetFiltersForm = customElements.get('facet-filters-form');
    if (!FacetFiltersForm || FacetFiltersForm.discountSortPatched) return;

    const originalRenderPage = FacetFiltersForm.renderPage;
    const originalRenderProductGridContainer =
      FacetFiltersForm.renderProductGridContainer;

    FacetFiltersForm.renderPage = function (
      searchParams,
      event,
      updateURLHash = true
    ) {
      const requestedParams = new URLSearchParams(searchParams);
      const requestedSort = requestedParams.get('sort_by');

      if (!isDiscountSort(requestedSort)) {
        return originalRenderPage.call(
          this,
          searchParams,
          event,
          updateURLHash
        );
      }

      const serverParams = new URLSearchParams(requestedParams);
      serverParams.delete('page');
      serverParams.set('sort_by', SERVER_SORT);

      const result = originalRenderPage.call(
        this,
        serverParams.toString(),
        event,
        false
      );

      if (updateURLHash) this.updateURLHash(requestedParams.toString());
      return result;
    };

    FacetFiltersForm.renderProductGridContainer = function (html) {
      const result = originalRenderProductGridContainer.call(this, html);
      queueDiscountSort();
      return result;
    };

    FacetFiltersForm.discountSortPatched = true;
  };

  patchFacetRendering();
  syncSortSelect();

  if (isDiscountSort(currentSort())) {
    queueDiscountSort();
  }
})();
